import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { BehaviorSubject, Observable, Subject, finalize, switchMap, tap } from 'rxjs';

import { GetAllLayersParams } from '@mirage-projects/cw-commons';
import {
    BrowserView,
    CatalogObjectSelection,
    Layer,
    ObjectBrowserFilter,
    ObjectBrowserSelectionTexts,
    ResultPage,
} from '@mirage-projects/cw-models';

import { GridColumn } from '../../models';
import { PaginationData } from '../../models/pagination-data';
import { SelectExistingLayerService } from './select-existing-layer.service';

type DefaultParams = Partial<GetAllLayersParams> & Required<Pick<GetAllLayersParams, 'page_size'>>;

/**
 * Shows the list of layers so the user selects one
 * It has pagination and filtering of the layers.
 */
@Component({
    selector: 'cw-select-existing-layer',
    templateUrl: 'select-existing-layer.component.html',
    styleUrls: ['./select-existing-layer.component.scss'],
    standalone: false,
})
export class SelectExistingLayerComponent implements OnInit {
    /** Hide the titlebar to use the components inside other ones that already have a title */
    @Input() hideTitlebar = false;
    /** Flag that tells the component it's being called by the wizard */
    @Input() isWizard = false;
    /** Enables select more than one element */
    @Input() isMultiSelection = true;

    @Input() defaultParams: DefaultParams = {
        layer_type: 'PointCloud',
        ordering: '-created_at',
        page_size: 18,
    };

    /** Emits when the user clicks on the close icon */
    @Output() closeClick = new EventEmitter<void>();

    footerSelectionResources: ObjectBrowserSelectionTexts = {
        selectedElementsText: 'LAYERS.SHOW_SELECTED',
        showSelectedText: 'LAYERS.SELECTED_LAYER',
        showSelectedsText: 'LAYERS.SELECTED_LAYERS',
    };

    loading = false;
    browserViews: BrowserView[] = [];
    columnsTable: GridColumn[] = [];
    hasToShowSelectedElements = false;

    private readonly data$ = new BehaviorSubject<void>(undefined);
    currentData$ = this.data$.pipe(switchMap(() => this.loadLayers()));

    private paginationSubject$: Subject<PaginationData> = new Subject();
    pagination$ = this.paginationSubject$.asObservable();

    private currentPage = 1;
    private filters: ObjectBrowserFilter[] = [];
    private params!: Partial<GetAllLayersParams>;

    constructor(
        public selectExistingLayerService: SelectExistingLayerService,
        private cdr: ChangeDetectorRef
    ) {}

    async ngOnInit(): Promise<void> {
        this.params = this.defaultParams;
        this.filters = this.selectExistingLayerService.initializeFilters();

        this.browserViews = [
            { id: 'grid', icon: 'service-menu', tooltip: 'OBJECT_BROWSER.GRID_VIEW', filters: this.filters },
            { id: 'list', icon: 'menu', tooltip: 'OBJECT_BROWSER.LIST_VIEW', filters: this.filters },
            { id: 'detail', icon: 'storage', tooltip: 'OBJECT_BROWSER.DETAIL_VIEW', filters: this.filters },
        ];

        this.initColumns();
    }

    onClose(): void {
        this.closeClick.emit();
    }

    /**
     * When filter is changed, data has to be refreshed
     * @param filter Filter to apply
     */
    filterChanged(filter: Map<string, string>): void {
        const filterData: Partial<GetAllLayersParams> = {};
        filter.forEach((value: string, key: string) => ((filterData as any)[key] = value));
        this.params = { ...this.defaultParams, page: this.currentPage, ...filterData };

        this.data$.next();
    }

    pageChanged(pageNumber: number): void {
        if (pageNumber > 0) {
            this.currentPage = pageNumber;
            this.params = { ...this.params, page: pageNumber };

            this.data$.next();
        }
    }

    dataRefreshed(): void {
        this.data$.next();
    }

    onShowSelectedElementsChanged(hasToShowSelectedElements: boolean): void {
        this.hasToShowSelectedElements = hasToShowSelectedElements;

        this.data$.next();
    }

    get defaultPagination(): PaginationData {
        return {
            currentPage: 1,
            pageSize: this.defaultParams.page_size,
            totalElements: 0,
        };
    }

    /**
     * Returns true if the layer is imcomplete
     * @param obj Current catalog object to check
     */
    hasToShowCheckbox(obj: CatalogObjectSelection): boolean {
        const layer = obj.catalogObject as Layer;

        return !layer.incomplete;
    }

    onLayerClicked(): void {
        if (this.hasToShowSelectedElements) {
            this.data$.next();
        }
    }

    get isElementSelectFilterShown(): boolean {
        const showWhenSelectedElements = !this.hasToShowSelectedElements && this.selectExistingLayerService.areLayersSelected();

        return this.hasToShowSelectedElements || showWhenSelectedElements;
    }

    private loadLayers(): Observable<ResultPage<CatalogObjectSelection>> {
        this.loading = true;
        this.cdr.detectChanges();

        const loadLayers$ = this.selectExistingLayerService.loadLayers(this.params).pipe(
            tap((result) => {
                this.paginationSubject$.next({
                    currentPage: this.currentPage,
                    pageSize: this.defaultParams.page_size,
                    totalElements: result.count,
                });
            }),
            finalize(() => (this.loading = false))
        );

        return loadLayers$;
    }

    private initColumns(): void {
        this.columnsTable = [
            {
                name: 'OBJECT_BROWSER.NAME',
                field: 'catalogObject.name',
                type: 'string',
                width: '55%',
                format: (row: CatalogObjectSelection) => this.selectExistingLayerService.getNameFormat(row),
            },
            { name: 'OBJECT_BROWSER.CREATED', field: 'catalogObject.createdAt', type: 'date', width: '30%' },
            {
                name: 'PROJECTS.TITLE',
                field: 'catalogObject.projectsCount',
                type: 'string',
                format: (row: CatalogObjectSelection) => this.selectExistingLayerService.getProjectsCount(row),
            },
            {
                name: '',
                field: 'selected',
                multiSelection: true,
                type: 'checkbox',
                isCellVisible: this.isCheckboxVisible,
            },
        ];
    }

    private isCheckboxVisible(_columnDefinition: GridColumn, row: CatalogObjectSelection): boolean {
        return !((row.catalogObject as Layer).incomplete ?? false);
    }
}
